// Per-user dashboard card preference: which cards are shown, and where they sit.
//
// The server owns the value (it follows the user across devices); the last
// saved preference is mirrored to localStorage and read as the initial state,
// so a hard refresh straight onto the dashboard paints the right cards in the
// right order instead of flashing the default arrangement while the fetch is in
// flight (the same no-flash pattern as sidebarThemeSlice).
//
// The root reducer (store/index.ts) resets this slice on logout; the matcher
// below also clears the localStorage mirror so the next user on a shared
// browser starts from the default (all cards visible, default layout).

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DashboardCardId,
  DashboardLayout,
  DEFAULT_DASHBOARD_LAYOUT,
  DEFAULT_HEIGHT_MODE,
  cacheHiddenCards,
  cacheLayout,
  clearCachedDashboardPrefs,
  readCachedHiddenCards,
  readCachedLayout,
} from '../config/dashboardCards';
import { dashboardPrefsService, DashboardPrefs } from '../services/dashboardPrefsService';

interface DashboardPrefsState {
  hidden: DashboardCardId[];
  layout: DashboardLayout;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  saving: boolean;
  error: string | null;
}

const initialState: DashboardPrefsState = {
  hidden: readCachedHiddenCards(),
  layout: readCachedLayout(),
  status: 'idle',
  saving: false,
  error: null,
};

const cache = (prefs: DashboardPrefs) => {
  cacheHiddenCards(prefs.hidden);
  cacheLayout(prefs.layout);
};

export const fetchDashboardPrefs = createAsyncThunk('dashboardPrefs/fetch', async () => {
  return await dashboardPrefsService.get();
});

export const saveDashboardPrefs = createAsyncThunk(
  'dashboardPrefs/save',
  async (prefs: DashboardPrefs) => {
    return await dashboardPrefsService.save(prefs);
  }
);

export const resetDashboardPrefs = createAsyncThunk('dashboardPrefs/reset', async () => {
  await dashboardPrefsService.reset();
});

const dashboardPrefsSlice = createSlice({
  name: 'dashboardPrefs',
  initialState,
  reducers: {
    // Optimistic local update, for surfaces that apply a change before the
    // server confirms it.
    setDashboardPrefsLocal(state, action: PayloadAction<DashboardPrefs>) {
      state.hidden = action.payload.hidden;
      state.layout = action.payload.layout;
      cache(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardPrefs.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDashboardPrefs.fulfilled, (state, action) => {
        state.status = 'loaded';
        state.hidden = action.payload.hidden;
        state.layout = action.payload.layout;
        cache(action.payload);
      })
      .addCase(fetchDashboardPrefs.rejected, (state, action) => {
        // Keep whatever is on screen; a failed read must not reshuffle cards.
        state.status = 'error';
        state.error = action.error.message ?? 'Failed to load dashboard preferences';
      })
      .addCase(saveDashboardPrefs.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveDashboardPrefs.fulfilled, (state, action) => {
        state.saving = false;
        state.status = 'loaded';
        state.hidden = action.payload.hidden;
        state.layout = action.payload.layout;
        cache(action.payload);
      })
      .addCase(saveDashboardPrefs.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? 'Failed to save dashboard preferences';
      })
      .addCase(resetDashboardPrefs.fulfilled, (state) => {
        state.hidden = [];
        state.layout = {
            main: [...DEFAULT_DASHBOARD_LAYOUT.main],
            rail: [...DEFAULT_DASHBOARD_LAYOUT.rail],
            spans: {},
            heightMode: DEFAULT_HEIGHT_MODE,
          };
        state.status = 'loaded';
        state.saving = false;
        cache({ hidden: state.hidden, layout: state.layout });
      })
      // End of session: drop to defaults and forget the cached preference, so
      // the next login on this browser doesn't wear the previous user's choices.
      .addMatcher(
        (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
        (state) => {
          state.hidden = [];
          state.layout = {
            main: [...DEFAULT_DASHBOARD_LAYOUT.main],
            rail: [...DEFAULT_DASHBOARD_LAYOUT.rail],
            spans: {},
            heightMode: DEFAULT_HEIGHT_MODE,
          };
          state.status = 'idle';
          state.saving = false;
          state.error = null;
          clearCachedDashboardPrefs();
        }
      );
  },
});

export const { setDashboardPrefsLocal } = dashboardPrefsSlice.actions;
export default dashboardPrefsSlice.reducer;
